/**
 * PostHog Analytics Utility - Issue #858
 *
 * Centralized analytics tracking for Ripia.
 * Safe to call even when PostHog is not initialized (non-production environments).
 */

import posthog from 'posthog-js';
import * as Sentry from '@sentry/react';
import { scrubPiiFields } from '@/utils/pii-scrubber';
import type { FeatureMinPlan } from '@/services/featureToggleApi';
import type { FeatureKey } from '@/components/plan/FeatureGate';

// Check if PostHog is properly initialized
const isPostHogInitialized = (): boolean => {
  try {
    return typeof posthog !== 'undefined' && posthog.__loaded === true;
  } catch {
    return false;
  }
};

/**
 * Track a custom event with optional properties
 */
export const trackEvent = (
  eventName: string,
  properties?: Record<string, unknown>
): void => {
  if (isPostHogInitialized()) {
    let scrubbedProps = properties;
    if (properties) {
      try {
        scrubbedProps = scrubPiiFields(properties);
      } catch (e) {
        console.warn('[Analytics] PII scrub failed, sending unscrubbed:', e);
      }
    }
    posthog.capture(eventName, scrubbedProps);
  }
};

/**
 * Identify a user (call after login)
 */
export const identifyUser = (
  userId: string,
  userProperties?: Record<string, unknown>
): void => {
  if (isPostHogInitialized()) {
    let scrubbedProps = userProperties;
    if (userProperties) {
      try {
        scrubbedProps = scrubPiiFields(userProperties);
      } catch (e) {
        console.warn('[Analytics] PII scrub failed, sending unscrubbed:', e);
      }
    }
    posthog.identify(userId, scrubbedProps);
  }
};

/**
 * Reset user identity (call on logout)
 */
export const resetUser = (): void => {
  if (isPostHogInitialized()) {
    posthog.reset();
  }
};

// ============================================
// Booking Flow Events
// ============================================

export const trackBookingStarted = (siteSlug: string): void => {
  trackEvent('booking_started', { site_slug: siteSlug });
};

export const trackBookingStepCompleted = (
  step: number,
  stepName: string,
  properties?: Record<string, unknown>
): void => {
  trackEvent('booking_step_completed', {
    step,
    step_name: stepName,
    ...properties,
  });
};

export const trackStaffSelected = (
  staffId: string,
  staffName: string
): void => {
  trackEvent('booking_staff_selected', {
    staff_id: staffId,
    staff_name: staffName,
  });
};

export const trackMenuSelected = (
  menuId: string,
  menuName: string,
  price: number
): void => {
  trackEvent('booking_menu_selected', {
    menu_id: menuId,
    menu_name: menuName,
    price,
  });
};

export const trackDateTimeSelected = (date: string, time: string): void => {
  trackEvent('booking_datetime_selected', {
    date,
    time,
  });
};

export const trackBookingCompleted = (
  reservationNumber: string,
  menuName: string,
  price: number,
  staffName?: string
): void => {
  trackEvent('booking_completed', {
    reservation_number: reservationNumber,
    menu_name: menuName,
    price,
    staff_name: staffName,
  });
};

export const trackBookingAbandoned = (
  lastStep: number,
  stepName: string
): void => {
  trackEvent('booking_abandoned', {
    last_step: lastStep,
    step_name: stepName,
  });
};

// ============================================
// Authentication Events
// ============================================

export const trackLoginSuccess = (method: string): void => {
  trackEvent('login_success', { method });
};

export const trackSignupCompleted = (method: string): void => {
  trackEvent('signup_completed', { method });
};

export const trackLogout = (): void => {
  try {
    trackEvent('logout');
    resetUser();
  } catch {
    // analytics失敗でログアウトフローを壊さない
  }
};

// ============================================
// Payment Events
// ============================================

export const trackPaymentInitiated = (
  amount: number,
  currency: string
): void => {
  trackEvent('payment_initiated', { amount, currency });
};

export const trackPaymentCompleted = (
  amount: number,
  currency: string,
  paymentMethod: string
): void => {
  trackEvent('payment_completed', {
    amount,
    currency,
    payment_method: paymentMethod,
  });
};

export const trackPaymentFailed = (
  amount: number,
  currency: string,
  errorMessage: string
): void => {
  trackEvent('payment_failed', {
    amount,
    currency,
    error_message: errorMessage,
  });
};

// ============================================
// BPaaS Events
// ============================================

export const trackFollowupSettingsChanged = (
  changes: Record<string, unknown>
): void => {
  trackEvent('followup_settings_changed', changes);
};

export const trackFollowupMessageSent = (segment: string): void => {
  trackEvent('followup_message_sent', { segment });
};

export const trackFollowupMessageSkipped = (reason: string): void => {
  trackEvent('followup_message_skipped', { reason });
};

export const trackChurnAlertViewed = (customerId: string): void => {
  trackEvent('churn_alert_viewed', { customer_id: customerId });
};

export const trackChurnAlertMessageGenerated = (customerId: string): void => {
  trackEvent('churn_alert_message_generated', { customer_id: customerId });
};

export const trackChurnAlertMessageSent = (customerId: string): void => {
  trackEvent('churn_alert_message_sent', { customer_id: customerId });
};

export const trackChurnAlertSkipped = (
  customerId: string,
  reason: string
): void => {
  trackEvent('churn_alert_skipped', {
    customer_id: customerId,
    reason,
  });
};

// ============================================
// Admin Operation Events
// ============================================

export const trackCustomerCreated = (bookingSource?: string): void => {
  trackEvent('customer_created', { booking_source: bookingSource });
};

export const trackReservationCreated = (source: string): void => {
  trackEvent('reservation_created', { source });
};

export const trackTreatmentRecorded = (customerId: string): void => {
  trackEvent('treatment_recorded', { customer_id: customerId });
};

export const trackAIAnalysisQuery = (): void => {
  trackEvent('ai_analysis_query');
};

export const trackSettingsUpdated = (section: string): void => {
  trackEvent('settings_updated', { section });
};

/**
 * Feature Toggle 切替 PostHog event。
 * PII 不含 (feature_key / boolean / plan literal のみ)。 顧客名・メール・電話・
 * LINE user ID 等を引数に追加してはならない。
 */
export const trackFeatureToggleChanged = (params: {
  feature_key: string;
  old_value: boolean;
  new_value: boolean;
  plan: FeatureMinPlan;
}): void => {
  trackEvent('feature_toggle_changed', {
    feature_key: params.feature_key,
    old_value: params.old_value,
    new_value: params.new_value,
    plan: params.plan,
  });
};

/**
 * UpgradePrompt dismiss event (PLG ファネル計測)。
 * dwell_ms は dialog open 時刻と close 時刻の差分 (ms)。
 */
export const trackUpgradeDialogDismissed = (params: {
  feature_key: string;
  dwell_ms: number;
}): void => {
  trackEvent('upgrade_dialog_dismissed', {
    feature_key: params.feature_key,
    dwell_ms: params.dwell_ms,
  });
};

export type PlanFunnelPlan = 'free' | 'light' | 'standard' | 'premium';
export type PaidPlan = Exclude<PlanFunnelPlan, 'free'>;
type BillingCycle = 'monthly' | 'annual';

export const trackCheckoutPageViewed = (params: {
  current_plan: PlanFunnelPlan;
  recommended_plan?: PaidPlan;
  tenant_id?: string;
  user_id?: string;
}): void => {
  trackEvent('checkout_page_viewed', {
    source: 'app_settings',
    current_plan: params.current_plan,
    ...(params.recommended_plan
      ? { recommended_plan: params.recommended_plan }
      : {}),
    ...(params.tenant_id ? { tenant_id: params.tenant_id } : {}),
    ...(params.user_id ? { user_id: params.user_id } : {}),
  });
};

export const trackCheckoutStarted = (params: {
  plan: PaidPlan;
  billing_cycle: BillingCycle;
  tenant_id: string;
  user_id: string;
  source: 'app_settings';
  coupon_code?: string;
}): void => {
  trackEvent('checkout_started', {
    plan: params.plan,
    billing_cycle: params.billing_cycle,
    tenant_id: params.tenant_id,
    user_id: params.user_id,
    source: params.source,
    ...(params.coupon_code ? { coupon_code: params.coupon_code } : {}),
  });
};

export const trackCheckoutCompleted = (params: {
  plan: PaidPlan;
  billing_cycle: BillingCycle;
  session_id: string;
  tenant_id: string;
  user_id: string;
  source: 'app_settings';
}): void => {
  trackEvent('checkout_completed', {
    plan: params.plan,
    billing_cycle: params.billing_cycle,
    session_id: params.session_id,
    tenant_id: params.tenant_id,
    user_id: params.user_id,
    source: params.source,
  });
};

/**
 * BPaaS 機能利用 funnel (checkout funnel と組み合わせ、 blocked → checkout_started →
 * checkout_completed の conversion attribution を可能にする)。
 *
 * - feature_used: どの機能を実利用したか (顧客価値の特定)
 * - feature_blocked_by_plan: どの機能で plan gate に弾かれたか (= upgrade 動機の Source)
 *
 * `extra` には PII を含めてはならない。 raw 値ではなく集計しやすい型 (length, boolean,
 * literal 等) のみ送る。 `trackEvent` 経由で scrubPiiFields も通るが、 call site 側で
 * 抑止するのが第一義。
 */
export type FeatureUsedName =
  | 'followup_queue'
  | 'churned_customers'
  | 'ai_chat'
  | 'booking_site'
  | 'customer_search';

/**
 * `plan` が PlanFunnelPlan literal でない場合に capture を skip し、 silent skew を
 * Sentry breadcrumb で観測可能化する。 PostHog dashboard に `(undefined)` バケットを
 * 流出させないための防衛線 (PR #3367 ensureTenantIdForPostHog と同パターン)。
 */
const ensurePlanFunnelPlan = (
  plan: unknown,
  helperName: string,
  fieldName: string
): plan is PlanFunnelPlan => {
  if (
    plan === 'free' ||
    plan === 'light' ||
    plan === 'standard' ||
    plan === 'premium'
  ) {
    return true;
  }
  Sentry.addBreadcrumb({
    category: 'analytics',
    level: 'warning',
    message: `${helperName} skipped: ${fieldName} is ${
      typeof plan === 'string' ? `'${plan}'` : String(plan)
    }`,
  });
  return false;
};

export const trackFeatureUsed = (params: {
  feature: FeatureUsedName;
  plan: PlanFunnelPlan;
  tenant_id?: string;
  user_id?: string;
  extra?: Record<string, unknown>;
}): void => {
  if (!ensurePlanFunnelPlan(params.plan, 'trackFeatureUsed', 'plan')) return;
  trackEvent('feature_used', {
    feature: params.feature,
    plan: params.plan,
    ...(params.tenant_id ? { tenant_id: params.tenant_id } : {}),
    ...(params.user_id ? { user_id: params.user_id } : {}),
    ...(params.extra ?? {}),
  });
};

export type FeatureBlockedSource = 'FeatureGate';

export const trackFeatureBlockedByPlan = (params: {
  // FEATURE_PLAN_MAP の key 集合に narrowing。 dashboard SSOT 不一致を型で予防。
  feature: FeatureKey;
  current_plan: PlanFunnelPlan;
  // required_plan は PaidPlan に絞る: 'free' は gate されないので
  // required_plan='free' は不可能イベント (PR #3367 trackCheckoutStarted と整合)。
  required_plan: PaidPlan;
  block_source: FeatureBlockedSource;
  tenant_id?: string;
  user_id?: string;
}): void => {
  if (
    !ensurePlanFunnelPlan(
      params.current_plan,
      'trackFeatureBlockedByPlan',
      'current_plan'
    )
  ) {
    return;
  }
  trackEvent('feature_blocked_by_plan', {
    feature: params.feature,
    current_plan: params.current_plan,
    required_plan: params.required_plan,
    block_source: params.block_source,
    ...(params.tenant_id ? { tenant_id: params.tenant_id } : {}),
    ...(params.user_id ? { user_id: params.user_id } : {}),
  });
};
